import { assertNever, definedMap } from "@glideapps/ts-necessities";
import { mapMap } from "collection-utils";
import { generateImageFromSeed } from "../common/components/triangle-image";
import type { GroundValue, LoadingValue, PrimitiveValue, Table } from "../common/computation-model/data";
import {
    arrayForEach,
    asMaybeDate,
    asMaybeNumber,
    asMaybeString,
    asPrimitive,
    asString,
    follow,
    isArrayValue,
    isLoadingValue,
    isPrimitive,
    isPrimitiveValue,
    isRow,
    isTable,
    tableForEach,
} from "../common/computation-model/data";
import type { Path, RelativePath, RootPath } from "../common/computation-model/paths";
import { getSymbolicRepresentationForPath } from "../common/computation-model/paths";
import type { Computation, Namespace, RootPathResolver } from "../common/computation-model/types";
import type { Formula, PrimitiveGlideTypeKind } from "../common/description";
import { formatDuration } from "../common/format-duration";
import { formatNumberFixed } from "../common/format-number";
import type {
    FormatDurationSpecification,
    FormatNumberSpecification,
} from "../generator/formulas/formula-specifications";
import { unparseMath } from "../generator/math";
import type { TextTemplateToken } from "../support";
import { assert, defined, isEmptyOrUndefined, mapFilterUndefined, tokenizeTemplateString } from "../support";
import type { Condition, ConditionValuePath, PathOrGroundValue } from "./conditions";
import {
    computeCondition,
    getPathsForCondition,
    getSymbolicRepresentationForCondition,
    getSymbolicRepresentationForPathOrGroundValue,
} from "./conditions";
import { getValueAt, makePathOrGroundValueGetter } from "./handlers";
import type { MathContext } from "./math-interpreter";
import { MathInterpreter } from "./math-interpreter";
import { DateTimeAsyncParser } from "./parse-date-time";

export class StaticTextTemplateComputation implements Computation {
    constructor(private readonly _tokens: readonly TextTemplateToken<Path>[]) {}

    public getPaths(): readonly Path[] {
        return mapFilterUndefined(this._tokens, t => (!t.isText ? t.value : undefined));
    }

    public compute(ns: RootPathResolver, context: GroundValue): GroundValue {
        const parts: string[] = [];
        for (const t of this._tokens) {
            if (t.isText) {
                parts.push(t.text);
            } else {
                const v = getValueAt(ns, context, t.value);
                if (isLoadingValue(v) || !isPrimitive(v)) continue;
                const s = asMaybeString(v);
                if (s === undefined) continue;
                parts.push(s);
            }
        }
        return parts.join("");
    }

    public get symbolicRepresentation(): string {
        const parts: string[] = [];
        for (const t of this._tokens) {
            if (t.isText) {
                parts.push(JSON.stringify(t.text));
            } else {
                parts.push(getSymbolicRepresentationForPath(t.value));
            }
        }
        return "(template " + parts.join("+") + ")";
    }
}

export class DynamicTextTemplateComputation implements Computation {
    constructor(private readonly _templatePath: Path, private readonly _replacementPaths: ReadonlyMap<string, Path>) {}

    public getPaths(): readonly Path[] {
        return [this._templatePath, ...this._replacementPaths.values()];
    }

    public compute(ns: RootPathResolver, context: GroundValue): GroundValue {
        const templateValue = getValueAt(ns, context, this._templatePath);
        if (isLoadingValue(templateValue)) return templateValue;
        const template = asMaybeString(asPrimitive(templateValue));
        if (template === undefined) return undefined;

        let loadingValue: LoadingValue | undefined;
        const tokens = tokenizeTemplateString(template, Array.from(this._replacementPaths.keys()));
        const parts = tokens.map(t => {
            if (t.isText) {
                return t.text;
            } else {
                const path = defined(this._replacementPaths.get(t.value));
                const v = getValueAt(ns, context, path);
                if (isLoadingValue(v)) {
                    loadingValue = v;
                    return;
                }
                return asString(v);
            }
        });
        if (loadingValue !== undefined) return loadingValue;
        return parts.join("");
    }

    public get symbolicRepresentation(): string {
        return `(template ${getSymbolicRepresentationForPath(this._templatePath)} ${Array.from(this._replacementPaths)
            .map(([k, p]) => `${k}: ${getSymbolicRepresentationForPath(p)}`)
            .join(" ")})`;
    }
}

export class FormatNumberComputation implements Computation {
    constructor(private readonly _numberPath: Path, private readonly _spec: FormatNumberSpecification) {}

    public getPaths(): readonly Path[] {
        return [this._numberPath];
    }

    public compute(ns: RootPathResolver, context: GroundValue): GroundValue {
        const numberValue = getValueAt(ns, context, this._numberPath);
        if (isLoadingValue(numberValue)) return numberValue;
        const n = asMaybeNumber(numberValue);
        if (n === undefined || isNaN(n)) return undefined;

        const spec = this._spec;
        // ##formatCurrency:
        // FIXME: Unify this code.  Until then, keep it in sync.
        if (spec.unit === "$" && !spec.unitAfterNumber) {
            return formatNumberFixed(n, spec.decimalsAfterPoint, spec.groupSeparator, "USD");
        } else {
            const s = formatNumberFixed(n, spec.decimalsAfterPoint, spec.groupSeparator, undefined);
            if (spec.unit === undefined) {
                return s;
            }
            if (spec.unitAfterNumber) {
                return s + spec.unit;
            } else {
                return spec.unit + s;
            }
        }
    }

    public get symbolicRepresentation(): string {
        return `(format-number ${getSymbolicRepresentationForPath(this._numberPath)})`;
    }
}

export class FormatDurationComputation implements Computation {
    constructor(private readonly _durationPath: Path, _spec: FormatDurationSpecification) {}

    public getPaths(): readonly Path[] {
        return [this._durationPath];
    }

    public compute(ns: RootPathResolver, context: GroundValue): GroundValue {
        const durationValue = getValueAt(ns, context, this._durationPath);
        if (isLoadingValue(durationValue)) return durationValue;
        const n = asMaybeNumber(durationValue);
        if (n === undefined || isNaN(n)) return undefined;

        return formatDuration(n);
    }

    public get symbolicRepresentation(): string {
        return `(format-duration ${getSymbolicRepresentationForPath(this._durationPath)})`;
    }
}

export class GenerateImageComputation implements Computation {
    constructor(private readonly _seedPath: Path, private readonly _isMesh: boolean) {}

    public getPaths(): readonly Path[] {
        return [this._seedPath];
    }

    public compute(resolver: RootPathResolver, context: GroundValue): GroundValue {
        const seedValue = getValueAt(resolver, context, this._seedPath);
        if (isLoadingValue(seedValue)) return seedValue;
        const seed = asString(seedValue);
        if (seed === "") return undefined;
        return generateImageFromSeed(seed, this._isMesh);
    }

    public get symbolicRepresentation(): string {
        return `(generate-image ${getSymbolicRepresentationForPath(this._seedPath)})`;
    }
}

export class MathComputation extends MathInterpreter implements Computation {
    constructor(
        formula: Formula,
        private readonly _variablePaths: ReadonlyMap<string, [Path, PrimitiveGlideTypeKind]>
    ) {
        super(
            formula,
            mapMap(_variablePaths, ([, k]) => k)
        );
    }

    public getPaths(): readonly Path[] {
        return Array.from(this._variablePaths.values()).map(([p]) => p);
    }

    public compute(ns: RootPathResolver, context: GroundValue, setAllDirty: () => void): GroundValue {
        const mathContext: MathContext = {
            setAllDirty,
            lookupVariable: n => {
                const p = this._variablePaths.get(n);
                if (p === undefined) return undefined;
                return getValueAt(ns, context, p[0]);
            },
        };
        return this.eval(mathContext);
    }

    public get symbolicRepresentation(): string {
        return `(math ${unparseMath(this._formula)} ${Array.from(this._variablePaths)
            .map(([k, [p]]) => `${k}: ${getSymbolicRepresentationForPath(p)}`)
            .join(" ")})`;
    }
}

export class ConditionComputation extends DateTimeAsyncParser implements Computation {
    constructor(private readonly _condition: Condition<ConditionValuePath>) {
        super();
    }

    public getPaths(): readonly Path[] {
        assert(getPathsForCondition(this._condition, true).length === 0);
        return getPathsForCondition(this._condition, false);
    }

    public compute(ns: RootPathResolver, context: GroundValue, setAllDirty: () => void): GroundValue {
        const get = makePathOrGroundValueGetter(ns, context, undefined);
        return computeCondition(this._condition, get, v => this.parseDateTime(v, setAllDirty));
    }

    public get symbolicRepresentation(): string {
        return getSymbolicRepresentationForCondition(this._condition);
    }
}

export interface IfThenElseClause {
    readonly condition: Condition<ConditionValuePath>;
    readonly then: PathOrGroundValue<ConditionValuePath>;
}

export class IfThenElseComputation extends DateTimeAsyncParser implements Computation {
    constructor(
        private readonly _clauses: readonly IfThenElseClause[],
        private readonly _else: PathOrGroundValue<ConditionValuePath>
    ) {
        super();
    }

    public getPaths(): readonly Path[] {
        const paths: Path[] = [];

        for (const c of this._clauses) {
            assert(getPathsForCondition(c.condition, true).length === 0);
            paths.push(...getPathsForCondition(c.condition, false));
            if (c.then.isPath) paths.push(c.then.path.path);
        }
        if (this._else.isPath) paths.push(this._else.path.path);

        return paths;
    }

    public compute(ns: RootPathResolver, context: GroundValue, setAllDirty: () => void): GroundValue {
        const get = makePathOrGroundValueGetter(ns, context, undefined);

        for (const c of this._clauses) {
            const result = computeCondition(c.condition, get, v => this.parseDateTime(v, setAllDirty));
            // `undefined` defaults to `false`
            if (result === true) {
                return c.then.isPath ? get(c.then.path) : c.then.value;
            }
        }
        return this._else.isPath ? get(this._else.path) : this._else.value;
    }

    public get symbolicRepresentation(): string {
        return `(if ${this._clauses
            .map(
                c =>
                    `(if: ${getSymbolicRepresentationForCondition(
                        c.condition
                    )} then: ${getSymbolicRepresentationForPathOrGroundValue(c.then, p => p.path)})`
            )
            .join(" ")} else: ${getSymbolicRepresentationForPathOrGroundValue(this._else, p => p.path)})`;
    }
}

export class SplitStringComputation implements Computation {
    constructor(private readonly _inputPath: Path, private readonly _separatorPath: Path) {}

    public getPaths(): readonly Path[] {
        return [this._inputPath, this._separatorPath];
    }

    public compute(ns: RootPathResolver, context: GroundValue): GroundValue {
        const inputValue = getValueAt(ns, context, this._inputPath);
        if (isLoadingValue(inputValue)) return inputValue;
        const input = asMaybeString(inputValue);

        const separatorValue = getValueAt(ns, context, this._separatorPath);
        if (isLoadingValue(separatorValue)) return separatorValue;
        const separator = asMaybeString(separatorValue);

        if (input === undefined || separator === undefined) return undefined;
        return input
            .split(separator)
            .map(s => s.trim())
            .filter(s => s !== "");
    }

    public get symbolicRepresentation(): string {
        return `(split-string ${getSymbolicRepresentationForPath(
            this._inputPath
        )} by: ${getSymbolicRepresentationForPath(this._separatorPath)})`;
    }
}

export class MakeArrayComputation implements Computation {
    constructor(private readonly _paths: readonly Path[]) {}

    public getPaths(): readonly Path[] {
        return this._paths;
    }

    public compute(ns: RootPathResolver, context: GroundValue): GroundValue {
        let loadingValue: LoadingValue | undefined;
        const result: PrimitiveValue[] = [];

        function push(v: GroundValue) {
            if (isLoadingValue(v)) {
                loadingValue = v;
                return;
            }

            if (isArrayValue(v)) {
                arrayForEach(v, push);
                return;
            }

            if (v === undefined || v === "") return;
            if (!isPrimitive(v)) return;
            result.push(v);
        }

        for (const p of this._paths) {
            push(getValueAt(ns, context, p));
        }

        if (loadingValue !== undefined) return loadingValue;
        return result;
    }

    public get symbolicRepresentation(): string {
        return `(make-array ${this._paths.map(getSymbolicRepresentationForPath).join(" ")})`;
    }
}

export class ConstructURLComputation implements Computation {
    constructor(
        private readonly _schemePath: Path,
        private readonly _hostPath: Path,
        private readonly _pathPath: Path,
        private readonly _queryParameterPaths: ReadonlyMap<string, Path>
    ) {}

    public getPaths(): readonly Path[] {
        return [this._schemePath, this._hostPath, this._pathPath, ...Array.from(this._queryParameterPaths.values())];
    }

    public compute(ns: RootPathResolver, context: GroundValue): GroundValue {
        const hostValue = getValueAt(ns, context, this._hostPath);
        if (isLoadingValue(hostValue)) return hostValue;
        const host = definedMap(hostValue, asString);
        if (isEmptyOrUndefined(host)) return undefined;

        const schemeValue = getValueAt(ns, context, this._schemePath);
        if (isLoadingValue(schemeValue)) return schemeValue;
        let scheme = definedMap(schemeValue, asString)?.trim();
        if (scheme === undefined || scheme === "") {
            scheme = "https";
        }

        const pathValue = getValueAt(ns, context, this._pathPath);
        if (isLoadingValue(pathValue)) return pathValue;
        let path = definedMap(pathValue, asString)?.trim();
        if (isEmptyOrUndefined(path)) {
            path = "/";
        } else if (!path.startsWith("/")) {
            path = "/" + path;
        }

        let loadingValue: LoadingValue | undefined;
        const query = mapFilterUndefined(this._queryParameterPaths.entries(), ([k, p]) => {
            const v = getValueAt(ns, context, p);
            if (isLoadingValue(v)) {
                loadingValue = v;
                return undefined;
            }
            const s = definedMap(v, asString);
            if (s === undefined) return undefined;
            return `${encodeURIComponent(k)}=${encodeURIComponent(s)}`;
        }).join("&");
        if (loadingValue !== undefined) return loadingValue;

        let url = `${scheme}://${host}${path}`;
        if (query.length > 0) {
            url = url + "?" + query;
        }

        return url;
    }

    public get symbolicRepresentation(): string {
        return `(construct-url scheme: ${getSymbolicRepresentationForPath(
            this._schemePath
        )} host: ${getSymbolicRepresentationForPath(this._hostPath)} path: ${getSymbolicRepresentationForPath(
            this._pathPath
        )} ${Array.from(this._queryParameterPaths)
            .map(([k, p]) => `${k}: ${getSymbolicRepresentationForPath(p)}`)
            .join(" ")} )`;
    }
}

// ##singleLookupViaCombine:
// FIXME: Do this via combine instead, clearing the whole table.
export class SingleLookupComputation implements Computation {
    // `_columnInRelationTablePath` is only here to dirty us if the looked-up
    // column changes.
    constructor(
        private readonly _relationPath: Path,
        private readonly _columnInRelationPath: RelativePath,
        private readonly _columnInRelationTablePath: RootPath | undefined
    ) {}

    public getPaths(): readonly Path[] {
        if (this._columnInRelationTablePath === undefined) {
            return [this._relationPath];
        } else {
            return [this._relationPath, this._columnInRelationTablePath];
        }
    }

    public compute(ns: Namespace, row: GroundValue): GroundValue {
        return follow(getValueAt(ns, row, this._relationPath), this._columnInRelationPath);
    }

    public get symbolicRepresentation(): string {
        return `(lookup ${getSymbolicRepresentationForPath(
            this._columnInRelationPath
        )} from: ${getSymbolicRepresentationForPath(this._relationPath)})`;
    }
}

export enum ModifyDateKind {
    StartOfDay = "start-of-day",
    EndOfDay = "end-of-day",
}

export class ModifyDateComputation implements Computation {
    constructor(private readonly _datePath: Path, private readonly _kind: ModifyDateKind) {}

    public getPaths(): readonly Path[] {
        return [this._datePath];
    }

    public compute(ns: Namespace, row: GroundValue): GroundValue {
        const dateValue = getValueAt(ns, row, this._datePath);
        if (isLoadingValue(dateValue)) return dateValue;

        const date = asMaybeDate(dateValue);
        if (date === undefined) return undefined;

        switch (this._kind) {
            case ModifyDateKind.StartOfDay:
                return date.startOfDay();
            case ModifyDateKind.EndOfDay:
                return date.endOfDay();
            default:
                return assertNever(this._kind);
        }
    }

    public get symbolicRepresentation(): string {
        return `(${this._kind} ${getSymbolicRepresentationForPath(this._datePath)})`;
    }
}

export enum ForwardingKind {
    LookupPrimitive = "lookup-primitive",
    LookupRow = "lookup-row",
    SingleValue = "single-value",
}

export class GlobalGuardedForwardComputation implements Computation {
    constructor(
        private readonly _forwardeePath: RootPath,
        private readonly _arbiterPath: Path,
        private readonly _kind: ForwardingKind
    ) {}

    public getPaths(): readonly Path[] {
        return [this._forwardeePath, this._arbiterPath];
    }

    public compute(ns: Namespace, row: GroundValue): GroundValue {
        const arbiter = getValueAt(ns, row, this._arbiterPath);
        if (isLoadingValue(arbiter)) return arbiter;

        const forwardee = ns.get(this._forwardeePath);
        if (isLoadingValue(forwardee)) return forwardee;

        if (this._kind === ForwardingKind.SingleValue) {
            if (arbiter === undefined) return undefined;
            if (isTable(arbiter) && arbiter.size === 0) return undefined;
            return forwardee;
        }

        if (isRow(arbiter)) {
            return forwardee;
        } else if (isTable(arbiter)) {
            const tableResult: Table = new Map();
            const primitiveResults: PrimitiveValue[] = [];
            tableForEach(arbiter, false, () => {
                if (forwardee === undefined) return;
                if (isRow(forwardee)) {
                    tableResult.set(forwardee.$rowID, forwardee);
                } else if (isArrayValue(forwardee)) {
                    arrayForEach(forwardee, v => {
                        if (v === undefined) return;
                        if (isPrimitiveValue(v)) {
                            primitiveResults.push(v);
                        }
                    });
                } else if (isPrimitiveValue(forwardee)) {
                    primitiveResults.push(forwardee);
                }
            });
            if (this._kind === ForwardingKind.LookupRow) {
                return tableResult;
            } else {
                return primitiveResults;
            }
        }

        return undefined;
    }

    public get symbolicRepresentation(): string {
        return `(guarded-forward ${getSymbolicRepresentationForPath(
            this._forwardeePath
        )} arbiter: ${getSymbolicRepresentationForPath(this._arbiterPath)} kind: ${this._kind})`;
    }
}
